import { fillMissingDates } from '@/utils/common/date'

import dayjs from 'dayjs'
import { queryOptions, type UndefinedInitialQueryOptions } from '@tanstack/vue-query'
import { api } from '@/services'
import { unwrapApiResponse } from '@queries/shared/unwrap-api-response'
import { useSystemVersionStore } from '@/stores/systemVersion.stores'
import { dashboardAccountInquiryKeys } from './keys'
import { normalizeV1EventRecords } from './normalize-event-record'

// v2 帳務清單 API 只回傳 startTime/endTime（OP#2487 起已是店家當地時間），沒有 date 欄位；
// fillMissingDates 的排序、補齊邏輯都是依 date 比對，缺了這欄會讓 a.date.localeCompare 對
// undefined 拋錯（v1 本身就有 date，維持原值）。直接取字串本身格式化，不做任何時區換算。
const withNormalizedDate = (records: any[]): any[] =>
  records.map((item) => ({
    ...item,
    date: item.date ?? dayjs(item.startTime).format('YYYY-MM-DDTHH:mm:ss')
  }))

type ClawOperationsDetailQueryOptions = UndefinedInitialQueryOptions<
  unknown,
  Error,
  unknown,
  ReturnType<typeof dashboardAccountInquiryKeys.clawOperationsDetail>
>

type ClawGoodsRecordQueryOptions = UndefinedInitialQueryOptions<
  unknown,
  Error,
  unknown,
  ReturnType<typeof dashboardAccountInquiryKeys.clawGoodsRecord>
>

type ClawEventRecordQueryOptions = UndefinedInitialQueryOptions<
  unknown,
  Error,
  unknown,
  ReturnType<typeof dashboardAccountInquiryKeys.clawEventRecord>
>

type CoinOperationsDetailQueryOptions = UndefinedInitialQueryOptions<
  unknown,
  Error,
  unknown,
  ReturnType<typeof dashboardAccountInquiryKeys.coinOperationsDetail>
>

type CoinEventRecordQueryOptions = UndefinedInitialQueryOptions<
  unknown,
  Error,
  unknown,
  ReturnType<typeof dashboardAccountInquiryKeys.coinEventRecord>
>

export const getClawOperationsDetailQueryOptions = (
  params: Parameters<typeof api.dashboardAccountInquiry.getClawOperationsDetail>[0],
  // 併入小時後，params.endDate 可能是「下一日期 00:00」這種涵蓋當天整天的邊界寫法（本身不是
  // 一個真正要補的日子）；fillMissingDates 要用的是實際查詢涵蓋到的最後一個日曆天，兩者不一定
  // 相同，所以獨立傳入，預設值 = params.endDate（沒有邊界位移時兩者本來就一樣）。
  // 傳 null 代表這次查詢不補值（使用者自選區間，如實呈現後端資料）
  fillEndDate: string | null = params.endDate
): ClawOperationsDetailQueryOptions => {
  const { isNewApi } = useSystemVersionStore()

  return queryOptions({
    queryKey: dashboardAccountInquiryKeys.clawOperationsDetail(params, isNewApi),
    queryFn: async ({ signal }): Promise<unknown> => {
      const response = isNewApi
        ? await api.dashboardAccountInquiry.getClawOperationsDetailV2(params, signal)
        : await api.dashboardAccountInquiry.getClawOperationsDetail(params, signal)

      const unwrapRes = unwrapApiResponse(response)

      if (unwrapRes?.records) {
        const normalizedRecords = withNormalizedDate(unwrapRes.records)

        // 帳務查詢頁本週預設（未套用篩選）要完整顯示本週日～本週六，但未來日期不會實際查詢
        // （避免後端拒絕），API 沒回的那幾天要靠 fillMissingDates 自己補上；v1 資料本身也可能
        // 有缺漏，同樣需要補齊。fillEndDate 為 null 代表已套用篩選，如實呈現後端資料
        unwrapRes.records =
          fillEndDate && normalizedRecords.length > 0
            ? fillMissingDates(
                normalizedRecords,
                params.startDate,
                fillEndDate,
                {
                  prizeWinCount: 0,
                  revenue: 0,
                  profit: unwrapRes.records[unwrapRes.records.length - 1].profit === null ? null : 0
                },
                isNewApi
              )
            : normalizedRecords
      }

      return unwrapRes
    }
  })
}

export const getClawGoodsRecordQueryOptions = (pcbId: string): ClawGoodsRecordQueryOptions => {
  const { isNewApi } = useSystemVersionStore()

  return queryOptions({
    queryKey: dashboardAccountInquiryKeys.clawGoodsRecord(pcbId, isNewApi),
    queryFn: async ({ signal }): Promise<unknown> => {
      const response = isNewApi
        ? await api.dashboardAccountInquiry.getClawGoodsRecordV2({ pcbId }, signal)
        : await api.dashboardAccountInquiry.getClawGoodsRecord({ pcbId }, signal)
      return unwrapApiResponse(response)
    }
  })
}

// 事件紀錄 V2 依日期區間全量回傳、不分頁，分頁與事件類型篩選都在前端做。分頁參數留在
// params 裡會進 query key，換頁就會重打 API（OP#2972 明確要求只在首次查詢／改日期／
// 重整時呼叫），所以在這裡剝掉。
const withoutPaging = (
  params: Parameters<typeof api.dashboardAccountInquiry.getClawEventRecord>[0]
) => {
  const { pageIndex, pageSize, ...rest } = params as Record<string, unknown>
  void pageIndex
  void pageSize
  return rest as typeof params
}

// V1 的 { event, eventLog, eventCode } 在這裡攤平成 V2 的 { date, type, payload }，
// 讓畫面不必依 isNewApi 維護兩套標題／描述／圖示／篩選邏輯。
const normalizeEventRecordResponse = (unwrapRes: any, isNewApi: boolean) => {
  if (isNewApi || !unwrapRes?.records) return unwrapRes

  return { ...unwrapRes, records: normalizeV1EventRecords(unwrapRes.records) }
}

export const getClawEventRecordQueryOptions = (
  params: Parameters<typeof api.dashboardAccountInquiry.getClawEventRecord>[0]
): ClawEventRecordQueryOptions => {
  const { isNewApi } = useSystemVersionStore()
  const queryParams = isNewApi ? withoutPaging(params) : params

  return queryOptions({
    queryKey: dashboardAccountInquiryKeys.clawEventRecord(queryParams, isNewApi),
    queryFn: async ({ signal }): Promise<unknown> => {
      const response = isNewApi
        ? await api.dashboardAccountInquiry.getClawEventRecordV2(queryParams, signal)
        : await api.dashboardAccountInquiry.getClawEventRecord(queryParams, signal)
      return normalizeEventRecordResponse(unwrapApiResponse(response), isNewApi)
    }
  })
}

export const getCoinOperationsDetailQueryOptions = (
  params: Parameters<typeof api.dashboardAccountInquiry.getCoinOperationsDetail>[0],
  // 併入小時後，params.endDate 可能是「下一日期 00:00」這種涵蓋當天整天的邊界寫法（本身不是
  // 一個真正要補的日子）；fillMissingDates 要用的是實際查詢涵蓋到的最後一個日曆天，兩者不一定
  // 相同，所以獨立傳入，預設值 = params.endDate（沒有邊界位移時兩者本來就一樣）。
  // 傳 null 代表這次查詢不補值（使用者自選區間，如實呈現後端資料）
  fillEndDate: string | null = params.endDate
): CoinOperationsDetailQueryOptions => {
  const { isNewApi } = useSystemVersionStore()

  return queryOptions({
    queryKey: dashboardAccountInquiryKeys.coinOperationsDetail(params, isNewApi),
    queryFn: async ({ signal }): Promise<unknown> => {
      const response = isNewApi
        ? await api.dashboardAccountInquiry.getCoinOperationsDetailV2(params, signal)
        : await api.dashboardAccountInquiry.getCoinOperationsDetail(params, signal)

      const unwrapRes = unwrapApiResponse(response)

      if (unwrapRes?.records) {
        // v1 欄位仍叫 exchangeCount，v2（DetailV2）已改名 exchangedCount（順便修正原本少一個 d 的拼字）；
        // 正規化成統一欄位名，避免下游依 isNewApi 各自判斷
        const normalizedRecords = withNormalizedDate(unwrapRes.records).map((item: any) => ({
          ...item,
          exchangedCount: item.exchangedCount ?? item.exchangeCount ?? 0
        }))

        // 帳務查詢頁本週預設（未套用篩選）要完整顯示本週日～本週六，但未來日期不會實際查詢
        // （避免後端拒絕），API 沒回的那幾天要靠 fillMissingDates 自己補上；v1 資料本身也可能
        // 有缺漏，同樣需要補齊。fillEndDate 為 null 代表已套用篩選，如實呈現後端資料
        unwrapRes.records =
          fillEndDate && normalizedRecords.length > 0
            ? fillMissingDates(
                normalizedRecords,
                params.startDate,
                fillEndDate,
                { exchangedCount: 0 },
                isNewApi
              )
            : normalizedRecords
      }

      return unwrapRes
    }
  })
}

export const getCoinEventRecordQueryOptions = (
  params: Parameters<typeof api.dashboardAccountInquiry.getCoinEventRecord>[0]
): CoinEventRecordQueryOptions => {
  const { isNewApi } = useSystemVersionStore()
  const queryParams = isNewApi ? withoutPaging(params) : params

  return queryOptions({
    queryKey: dashboardAccountInquiryKeys.coinEventRecord(queryParams, isNewApi),
    queryFn: async ({ signal }): Promise<unknown> => {
      const response = isNewApi
        ? await api.dashboardAccountInquiry.getCoinEventRecordV2(queryParams, signal)
        : await api.dashboardAccountInquiry.getCoinEventRecord(queryParams, signal)
      return normalizeEventRecordResponse(unwrapApiResponse(response), isNewApi)
    }
  })
}
